import { CoreImageView } from '@core/widgets';
import { useApp, useBehavior, useTriggerAction } from '@libs/hooks';
import { useConsumerInput } from '../../hooks/useInputs';
import { AssetsSvgMap, TItem, TWidget, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, ConditionalRender, eventBus, getFieldName } from '@libs/utils';
import React, { useId, useEffect, useRef, useState } from 'react';
import { TConsumerItem, TConsumerListData, TDropdownData } from '../../types/data';
import { ConsumerActionPlayerNameEnums, RowEnum } from '../../types/enums';
import { TDropdownOption, TListOption } from '../../types/options';
import { getHref } from '../Image/imageUtils';
import { ListSelectView } from '../List/select/ListSelectView';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { Modal } from '../UI/Modal/Modal';
import { TextCommon } from '../UI/Text/TextCommon';

const Dropdown = ({ widget }: TWidgetProps<TDropdownData, TDropdownOption>) => {
    const id = useId();
    const data = widget.data ?? {};
    const messages = data.messages ?? {};
    const options = widget.options ?? {};
    const label = messages.label || data.label;
    const selectItemTitle = messages.selectItemTitle || label;
    const name = getFieldName(options.autofill);
    const inputAutoComplete = camelCaseToKebabCase(options.autofill ?? 'off');
    const [lockSource, setLockSource] = useState('');
    const useAppHook = useApp();
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { handleClickDropdown, isFilled, isError, value, ref } = useConsumerInput(widget, {
        isHidden,
        isDisabled,
        triggerActionsByType,
        setModal: useAppHook.setModal,
    });
    const hasItems = Array.isArray(widget.data.items) && widget.data.items.length > 0;

    const dataForListSelect = (
        lockSource ? { ...widget.data, items: (widget.data.items ?? []).filter((item) => item.source === lockSource) } : widget.data
    ) as TConsumerListData;

    // Keep the latest values in refs so the eventBus subscription can be created once (on internalId change)
    // instead of on every render/lockSource change. The eventBus is a ReplaySubject that re-delivers recent
    // events to new subscribers, so re-subscribing on every lockSource change would re-process stale
    // lock_source events and cause an oscillation loop when the source is switched quickly.
    const lockSourceRef = useRef(lockSource);
    const handleClickDropdownRef = useRef(handleClickDropdown);
    useEffect(() => {
        lockSourceRef.current = lockSource;
        handleClickDropdownRef.current = handleClickDropdown;
    });

    useEffect(() => {
        const internalId = widget.internalId;
        if (!internalId) {
            return;
        }
        eventBus.subscribeOnSpecificWidget(ConsumerActionPlayerNameEnums.lock_source, internalId, (value) => {
            if (value !== lockSourceRef.current) {
                handleClickDropdownRef.current({} as TItem, { doNotValidate: true });
                setLockSource(value as string);
            }
        });
        return () => {
            eventBus.unsubscribeSpecificWidget(ConsumerActionPlayerNameEnums.lock_source, internalId);
        };
    }, [widget.internalId]);

    const onSelectItem = (_wid: string, item: TConsumerItem) => {
        if (hasItems && item) {
            useAppHook.setModal(null);
            handleClickDropdown(item);
        }
    };
    const getTitleByValue = (selectedValue?: string): string => {
        if (selectedValue) {
            const title = data.items?.filter((i) => i.value === selectedValue)[0]?.title;
            return title ?? value;
        }
        return '';
    };

    const modalContent = (
        <Modal isCanCloseModal={true} onHandleClick={() => useAppHook.setModal(null)}>
            <ConditionalRender condition={!!selectItemTitle}>
                <TextCommon
                    isWidget={false}
                    uid={'header_' + widget.uid}
                    options={{ variant: 'header', margin: { top: 16, left: 24, right: 24, bottom: 32 } }}
                    content={selectItemTitle}
                />
            </ConditionalRender>
            <ListSelectView
                rowId={RowEnum.SELECTOR_REGULAR}
                widget={widget as TWidget<TConsumerListData, TListOption>}
                isOpenInModal={true}
                onSelectItem={onSelectItem}
                isHidden={false}
                isDisabled={false}
                dataForListSelect={dataForListSelect}
            />
        </Modal>
    );

    const openModal = () => {
        if (!isDisabled) {
            useAppHook.setModal(modalContent);
        }
    };

    return (
        <InputWrapper
            dataTest={widget.uid}
            isDropdownInput
            widget={widget}
            htmlFor={id}
            isDisabled={isDisabled}
            isHidden={isHidden}
            labelText={label}
            isError={isError}
            errorMessage={messages?.errorMessage ?? data.errorMessage}
            isFilled={isFilled}
            helperText={data.helperText}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
            rightInputComponent={
                <div onClick={openModal}>
                    <CoreImageView
                        src={getHref(AssetsSvgMap.ic_chevron_down)}
                        styleProps={{
                            iconColor: `var(--color-${isDisabled ? 'grey_40_lc' : 'black'})`,
                        }}
                    />
                </div>
            }
        >
            <input
                ref={ref}
                id={id}
                disabled={isDisabled}
                name={name}
                autoComplete={inputAutoComplete}
                required={data.mandatory ?? false}
                value={getTitleByValue(value)}
                readOnly={true}
                onClick={openModal}
                onFocus={openModal}
                tabIndex={data.tabIndex}
                inputMode="text"
                type="text"
            />
        </InputWrapper>
    );
};
export { Dropdown };
